import type { ReactNode } from 'react'
import { ApiError } from '../../lib/api'
import { Notice } from './Notice'

/** `.empty` — a dead end that tells the user what to do next. */
export function EmptyState({
  icon,
  title,
  body,
  action,
}: {
  icon?: ReactNode
  title: string
  body?: ReactNode
  action?: ReactNode
}) {
  return (
    <div className="empty">
      {icon}
      <p className="empty__title">{title}</p>
      {body ? <p className="empty__body">{body}</p> : null}
      {action ? <div className="empty__action">{action}</div> : null}
    </div>
  )
}

/**
 * Shape-preserving placeholders. Never a spinner where the final shape is
 * already known — a spinner tells the user nothing about what is arriving.
 */
export function SkeletonRows({ rows = 5, label }: { rows?: number; label: string }) {
  return (
    <div className="record-list">
      <p className="u-visually-hidden" role="status">
        {label}
      </p>
      {Array.from({ length: rows }, (_, index) => (
        <div className="record-list__item" key={index} aria-hidden="true">
          <div className="skeleton skeleton--title" />
          <div className="skeleton skeleton--line" />
          <div className="skeleton skeleton--short" />
        </div>
      ))}
    </div>
  )
}

/** Route-level loading. `role="status"` so the change is announced. */
export function RouteLoading({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="state-page">
      <span className="btn__spinner" aria-hidden="true" />
      <p className="u-visually-hidden" role="status">
        {label}
      </p>
      <p className="u-muted">{label}…</p>
    </div>
  )
}

/**
 * The standard failure block: a plain sentence, a retry, and the request id in
 * caption text so support can find the log line. Never an HTTP code as the
 * headline.
 */
export function ErrorState({
  error,
  onRetry,
  title = "We couldn't load this",
}: {
  error: unknown
  onRetry?: () => void
  title?: string
}) {
  const apiError = error instanceof ApiError ? error : null
  return (
    <div className="u-stack">
      <Notice tone="danger" title={title} role="alert">
        <p>
          {apiError?.isOffline
            ? "You're offline. Check your connection and try again."
            : (apiError?.detail ?? 'This one is on us, not you. Try again in a moment.')}
        </p>
        {apiError?.requestId ? (
          <p className="u-caption">Reference: {apiError.requestId}</p>
        ) : null}
      </Notice>
      {onRetry ? (
        <div>
          <button className="btn btn--secondary" type="button" onClick={onRetry}>
            Try again
          </button>
        </div>
      ) : null}
    </div>
  )
}
